import React, { memo } from 'react'

import {
  FormOnChange,
  MyOptionType,
  Select,
  SelectProps,
  Stack,
  Text,
  applyDefaultSelectFilter,
} from '@procsea/design-system'

import { RequestableProduct } from 'src/types'

import { getAllRequestableProductOptions } from './RequestableProductSelector.utils'
import useRequestableProductOptions from './useRequestableProductOptions'

const formatSellerProductOption = ({
  label,
  latinName,
}: MyOptionType<Id> & { latinName: string }) => (
  <Stack>
    <Text>{label}</Text>
    <Text variant="neutral" size="small" italic>
      {latinName}
    </Text>
  </Stack>
)

interface RequestableProductSelectorProps {
  'data-e2e'?: string
  categoryId?: Id
  errorMessage?: string
  fluid?: boolean
  label?: string
  name?: string
  onChange?: FormOnChange<MyOptionType<RequestableProduct> | null>
  required?: SelectProps<MyOptionType<RequestableProduct>, false>['required']
  value?: MyOptionType<RequestableProduct> | null
  verticalId?: Id
}

const RequestableProductSelector = ({
  'data-e2e': dataE2e = 'requestable-product-select',
  categoryId,
  errorMessage,
  fluid = false,
  label,
  name,
  onChange,
  required,
  value,
  verticalId,
}: RequestableProductSelectorProps) => {
  const { requestableProductOptions, isInitialLoading } = useRequestableProductOptions({
    categoryId,
    verticalId,
  })

  const allRequestableProductOptions = getAllRequestableProductOptions({
    requestableProductOptions,
    value,
  })

  const handleFilterOption = (
    option: {
      label: string
      value: MyOptionType<RequestableProduct>
    },
    inputValue: string
  ) => applyDefaultSelectFilter(option as any, inputValue) || option.value.value.isFallbackProduct

  return (
    <Select<MyOptionType<RequestableProduct>, false>
      clearable
      data-e2e={dataE2e}
      disabled={
        !categoryId || !allRequestableProductOptions || allRequestableProductOptions.length === 0
      }
      errorMessage={errorMessage}
      filterOption={handleFilterOption as any}
      fluid={fluid}
      formatOptionLabel={formatSellerProductOption as any}
      formatValueLabel={formatSellerProductOption as any}
      isLoading={isInitialLoading}
      label={label}
      name={name}
      onChange={onChange}
      options={allRequestableProductOptions}
      placeholder={gettext('Select a product')}
      required={required}
      value={value}
      warningMessage={!!categoryId ? undefined : gettext('Select a category')}
    />
  )
}

export default memo(RequestableProductSelector)
